import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  BUCKET_LABEL,
  BUCKET_ORDER,
  type Bucket,
  deleteOverride,
  type EmailDetail,
  postOverride,
  type Priority,
} from "@/lib/emails";

const PRIORITIES: Priority[] = ["P0", "P1", "P2", "P3"];

interface ReclassifyPanelProps {
  email: EmailDetail;
  /** Called with the updated detail after a successful save or reset. */
  onUpdated: (updated: EmailDetail) => void;
}

type SaveState =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "error"; message: string };

export function ReclassifyPanel({ email, onUpdated }: ReclassifyPanelProps) {
  // Local form state seeded from the email — reset when the email changes.
  const [bucket, setBucket] = useState<Bucket>(email.bucket);
  const [priority, setPriority] = useState<Priority>(email.priority);
  const [state, setState] = useState<SaveState>({ kind: "idle" });

  useEffect(() => {
    setBucket(email.bucket);
    setPriority(email.priority);
    setState({ kind: "idle" });
  }, [email.id, email.bucket, email.priority]);

  const changed = bucket !== email.bucket || priority !== email.priority;
  const saving = state.kind === "saving";

  const handleSave = async () => {
    if (!changed || saving) return;
    setState({ kind: "saving" });
    const payload: { bucket?: Bucket; priority?: Priority } = {};
    if (bucket !== email.bucket) payload.bucket = bucket;
    if (priority !== email.priority) payload.priority = priority;
    try {
      const updated = await postOverride(email.id, payload);
      onUpdated(updated);
      setState({ kind: "idle" });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setState({ kind: "error", message });
    }
  };

  const handleReset = async () => {
    if (saving) return;
    setState({ kind: "saving" });
    try {
      const updated = await deleteOverride(email.id);
      onUpdated(updated);
      setState({ kind: "idle" });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setState({ kind: "error", message });
    }
  };

  return (
    <section
      data-testid="reclassify-panel"
      className="rounded-lg border border-op-border bg-op-surface px-4 py-3"
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-[10px] uppercase tracking-wider text-op-muted">
          Reclassify
        </h3>
        {email.is_overridden ? (
          <span
            data-testid="overridden-badge"
            className="rounded-full border border-op-border-accent bg-op-accent-muted px-2 py-0.5 text-[10px] uppercase tracking-wider text-op-accent"
          >
            Pinned by you
          </span>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-xs text-op-secondary">
          <span className="uppercase tracking-wider text-op-muted">Bucket</span>
          <select
            data-testid="reclassify-bucket"
            value={bucket}
            disabled={saving}
            onChange={(e) => setBucket(e.target.value as Bucket)}
            className="rounded border border-op-border bg-op-base px-2 py-1.5 text-sm text-op-primary focus:outline-none focus:ring-2 focus:ring-op-accent"
          >
            {BUCKET_ORDER.map((b) => (
              <option key={b} value={b}>
                {BUCKET_LABEL[b]}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs text-op-secondary">
          <span className="uppercase tracking-wider text-op-muted">
            Priority
          </span>
          <select
            data-testid="reclassify-priority"
            value={priority}
            disabled={saving}
            onChange={(e) => setPriority(e.target.value as Priority)}
            className="rounded border border-op-border bg-op-base px-2 py-1.5 text-sm text-op-primary focus:outline-none focus:ring-2 focus:ring-op-accent"
          >
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3">
        <Button
          data-testid="reclassify-save"
          size="sm"
          disabled={!changed || saving}
          onClick={handleSave}
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
        {email.is_overridden ? (
          <button
            data-testid="reclassify-reset"
            type="button"
            disabled={saving}
            onClick={handleReset}
            className="text-xs text-op-secondary underline-offset-2 hover:text-op-primary hover:underline disabled:opacity-50"
          >
            Reset to AI's call
          </button>
        ) : null}
      </div>

      {state.kind === "error" ? (
        <p
          role="alert"
          data-testid="reclassify-error"
          className="mt-2 text-xs text-op-secondary"
        >
          Couldn't save ({state.message}). Try again.
        </p>
      ) : null}
    </section>
  );
}
